import config from 'src/config';
import apiService, { HTTPMethod } from 'src/services/api-service';
import authService from 'src/services/auth-service';

type DeleteFilesRequest = {
    ids: number[];
};

type RenameFileRequest = {
    id: number;
    newName: string;
};

type UploadFileRequest = {
    projectId?: string;
    portfolioId?: string;
    file: File | Blob;
    fileName?: string;
};

type DownloadFilesRequest = {
    ids: number[];
};

type DownloadFilesResponse = {
    blob: Blob;
    filename?: string;
};

type DownloadReportRequest = {
    fileName: string;
    projectId: string;
};

type GetImageRequest = {
    projectId: string;
    fileId: string | number;
};

class FileApiService {
    private async getAuthHeader() {
        const token = await authService.getAccessToken();
        if (!token) {
            throw new Error('Not authenticated');
        }
        return token;
    }

    public async deleteFiles(request: DeleteFilesRequest): Promise<any> {
        const authHeader = await this.getAuthHeader();
        const url = `${config.apiBaseUrl}/reporting-hub/delete`;

        const response = await apiService.fetch(url, {
            method: HTTPMethod.POST,
            headers: {
                'Content-Type': 'application/json',
                Accept: 'text/plain',
                Authorization: authHeader,
            },
            body: JSON.stringify({ ids: request.ids }),
            noCache: true,
        });

        return response;
    }

    public async renameFile(request: RenameFileRequest): Promise<any> {
        const authHeader = await this.getAuthHeader();
        const url = `${config.apiBaseUrl}/reporting-hub/rename`;

        const response = await apiService.fetch(url, {
            method: HTTPMethod.POST,
            headers: {
                'Content-Type': 'application/json',
                Accept: 'text/plain',
                Authorization: authHeader,
            },
            body: JSON.stringify({ id: request.id, newName: request.newName }),
            noCache: true,
        });

        return response;
    }

    public async uploadFile(request: UploadFileRequest): Promise<any> {
        const authHeader = await this.getAuthHeader();
        const url = `${config.apiBaseUrl}/reporting-hub/upload`;

        const formData = new FormData();
        if (request.projectId) {
            formData.append('projectId', request.projectId);
        }
        if (request.portfolioId) {
            formData.append('portfolioId', request.portfolioId);
        }
        const fileToUpload = request.file as File;
        const fileField: Blob = fileToUpload;
        const fileName = request.fileName || (fileToUpload && (fileToUpload as File).name) || 'upload.bin';
        formData.append('file', fileField, fileName);

        const nativeFetch = apiService.getFetch();
        const response = await nativeFetch(url, {
            method: 'POST',
            headers: {
                Authorization: authHeader,
            },
            body: formData,
        });

        if (!response.ok) {
            throw new Error(`Upload failed with status ${response.status}`);
        }

        const contentType = response.headers.get('Content-Type') || '';
        if (contentType.includes('application/json')) {
            return await response.json();
        }

        return await response.text();
    }

    public async downloadFiles(request: DownloadFilesRequest): Promise<DownloadFilesResponse> {
        const authHeader = await this.getAuthHeader();
        const url = `${config.apiBaseUrl}/reporting-hub/download`;

        const nativeFetch = apiService.getFetch();
        const response = await nativeFetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Authorization: authHeader,
            },
            body: JSON.stringify({ ids: request.ids }),
        });

        if (!response.ok) {
            throw new Error(`Download failed with status ${response.status}`);
        }

        const disposition = response.headers.get('Content-Disposition') || '';
        const match = disposition.match(/filename\*=UTF-8''([^;\n\r]+)/) || disposition.match(/filename="?([^";\n\r]+)"?/);
        const filename = match && match[1] ? decodeURIComponent(match[1]) : undefined;

        const blob = await response.blob();
        return { blob, filename };
    }

    public async downloadReport(request: DownloadReportRequest): Promise<DownloadFilesResponse> {
        const authHeader = await this.getAuthHeader();
        const url = `${config.apiBaseUrl}/reporting-hub/report-download?sampling_event=${request.fileName}&projectId=${request.projectId}`;

        const nativeFetch = apiService.getFetch();
        const response = await nativeFetch(url, {
            method: 'GET',
            headers: {
                Authorization: authHeader,
            },
        });

        if (!response.ok) {
            throw new Error(`Report download failed with status ${response.status}`);
        }

        const blob = await response.blob();
        return { blob };
    }

    public async getImageForSiteFilesPreview(request: GetImageRequest): Promise<Blob> {
        const authHeader = await this.getAuthHeader();
        const url = `${config.apiBaseUrl}/reporting-hub/${request.projectId}/image/${request.fileId}`;

        const nativeFetch = apiService.getFetch();
        const response = await nativeFetch(url, {
            method: 'GET',
            headers: {
                Authorization: authHeader,
            },
        });

        if (!response.ok) {
            throw new Error(`Image download failed with status ${response.status}`);
        }

        return await response.blob();
    }
}

const fileApiService = new FileApiService();
export default fileApiService;
export type {
    DeleteFilesRequest,
    RenameFileRequest,
    UploadFileRequest,
    DownloadFilesRequest,
    DownloadFilesResponse,
    DownloadReportRequest,
    GetImageRequest,
};
